/**
 * AgentDisposeModal.tsx
 *
 * Operator-facing dispose flow for AI agents (Phase 2 / slice 1c).
 *
 * Decommissions a registered agent in two coordinated steps:
 *
 *   1. ASSET SWEEP — every classic-Stellar asset on the agent's
 *      account (XLM + trustline tokens) is transferred to a target
 *      wallet chosen by the operator. By default the target is the
 *      operator's own G-wallet; when the operator has a passkey
 *      smart-account linked (`C…`) we surface a "Send to passkey
 *      wallet" chip so funds can land directly in the contract account.
 *      Optional sub-steps:
 *        · close all token trustlines (frees up reserve XLM)
 *        · accountMerge into the target (closes the agent G-account
 *          on-ledger and refunds the base reserves)
 *
 *   2. REVOKE — the operator wallet signs `revoke_agent` on the
 *      registry contract. The address is permanently retired (per
 *      slice 1 design: agent addresses can't be re-registered).
 *
 * The frontend does NOT possess the agent's secret key (it's stored
 * locally in the MCP credential store), so the actual sweep must
 * execute through the MCP `agent_dispose` tool. This modal acts as
 * a planning + confirmation surface: it enumerates balances live via
 * `/api/v2/agents/:address/balances`, lets the operator preview the
 * exact set of operations, and provides the parameters that the
 * matching MCP invocation needs.
 *
 * Related files:
 *   - server/v2/routes/agents.js                    GET /:address/balances
 *   - geoai/lib/geotrust-asset-sweep.mjs            sweep enumerator + tx builder
 *   - geoai/lib/geotrust-agent-dispose.mjs          orchestrator
 *   - mcp-server/src/tools.js                       agent_dispose tool
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAgentBalances,
  type AgentBalances,
  type AgentBalanceCredit,
} from '../services/agentService';
import type { PasskeySigner } from '../services/profileService';

interface AgentDisposeModalProps {
  open: boolean;
  agentAddress: string;
  /** Operator's `G…` (or `C…`) wallet — default sweep target. */
  operatorWallet: string;
  /** Optional passkey signer; if its `account.accountAddress` differs from
   *  `operatorWallet` we surface the "send to passkey" chip. */
  passkey: PasskeySigner | null;
  /** Called once the operator confirms — parent triggers revoke + refresh. */
  onConfirmed: (params: DisposeParams) => void;
  onClose: () => void;
}

export interface DisposeParams {
  /** The wallet the agent's funds will be swept to. */
  targetAccountId: string;
  /** Whether the sweep should additionally close trustlines. */
  closeTrustlines: boolean;
  /** Whether the sweep should accountMerge the agent's G-account. */
  mergeAccount: boolean;
}

const STELLAR_G = /^G[A-Z2-7]{55}$/;
const STELLAR_C = /^C[A-Z2-7]{55}$/;

const AgentDisposeModal: React.FC<AgentDisposeModalProps> = ({
  open,
  agentAddress,
  operatorWallet,
  passkey,
  onConfirmed,
  onClose,
}) => {
  const [balances, setBalances]     = useState<AgentBalances | null>(null);
  const [loading,  setLoading]      = useState(false);
  const [err,      setErr]          = useState<string | null>(null);
  const [target,   setTarget]       = useState<string>(operatorWallet);
  const [closeTL,  setCloseTL]      = useState<boolean>(true);
  const [merge,    setMerge]        = useState<boolean>(true);
  const [acked,    setAcked]        = useState<boolean>(false);

  // The operator may have a passkey smart-account linked. We only
  // surface the "send to passkey" chip when the passkey account is
  // a real C-address AND it differs from the operator wallet.
  const passkeyTarget = useMemo<string | null>(() => {
    const acct = passkey?.account?.accountAddress;
    if (!acct || !STELLAR_C.test(acct)) return null;
    if (acct === operatorWallet) return null;
    return acct;
  }, [passkey, operatorWallet]);

  // Reset local state every time the modal opens for a different agent.
  useEffect(() => {
    if (!open) return;
    setTarget(operatorWallet);
    setCloseTL(true);
    setMerge(true);
    setAcked(false);
    setErr(null);
  }, [open, operatorWallet, agentAddress]);

  const refresh = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setErr(null);
    try {
      const b = await getAgentBalances(agentAddress);
      setBalances(b);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load balances');
      setBalances(null);
    } finally {
      setLoading(false);
    }
  }, [open, agentAddress]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Validate the manually-typed (or chip-set) target address. We
  // accept G… and C… — the geoai sweep script also accepts both.
  const targetValid = STELLAR_G.test(target) || STELLAR_C.test(target);

  // Disable merge if the target isn't a classic account (accountMerge
  // requires destination to be a G-address). Auto-uncheck so the
  // confirm payload doesn't leak the contradiction.
  useEffect(() => {
    if (target && !STELLAR_G.test(target) && merge) setMerge(false);
  }, [target, merge]);

  const opsCount = useMemo(() => {
    if (!balances || balances.unfunded) return 0;
    let n = 0;
    n += balances.credits.length;                 // payment per trustline
    if (closeTL) n += balances.credits.length;    // changeTrust per token
    if (balances.native) n += 1;                  // sweep XLM
    if (merge && STELLAR_G.test(agentAddress)) n += 1; // accountMerge
    return n;
  }, [balances, closeTL, merge, agentAddress]);

  // Hard guards before we let the operator confirm. Sweep refuses
  // when the agent has open offers / data entries OR when any
  // trustline carries selling liabilities (would orphan an offer).
  const blockingReasons = useMemo<string[]>(() => {
    const reasons: string[] = [];
    if (!balances) return reasons;
    if (balances.unfunded) {
      // No assets to sweep — the only meaningful action is revoke.
      // We don't *block* dispose, the parent just skips the sweep.
      return reasons;
    }
    if (balances.hasOpenOffersOrData) {
      reasons.push('Agent has open offers or data entries — clean those up before sweeping.');
    }
    for (const c of balances.credits) {
      const sell = parseFloat(c.sellingLiabilities || '0');
      if (sell > 0) {
        reasons.push(`${c.code} has ${c.sellingLiabilities} selling liabilities (open offer).`);
      }
    }
    return reasons;
  }, [balances]);

  const canConfirm =
    !loading
    && !!balances
    && targetValid
    && acked
    && blockingReasons.length === 0;

  if (!open) return null;

  const isClassicAgent = STELLAR_G.test(agentAddress);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dispose-title"
      style={overlayStyle}
      onClick={onClose}
    >
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <div>
            <div style={kickerStyle}>Decommission AI agent</div>
            <h3 id="dispose-title" style={titleStyle}>Dispose agent</h3>
            <div style={agentAddrStyle} title={agentAddress}>{agentAddress}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
        </header>

        <p style={leadStyle}>
          Sweeps every asset on the agent's wallet to a target you choose, then revokes the agent on
          the registry. The address is retired permanently — register a fresh agent if you need
          another identity later.
        </p>

        {!isClassicAgent && (
          <div style={infoBoxStyle}>
            This agent is a smart-account (<code>C…</code>). Asset enumeration through Horizon is
            not available for contract accounts — sweep will route through the dedicated Soroban
            path (not in this build). You can still revoke the agent on the registry.
          </div>
        )}

        {/* ── Balances ───────────────────────────────────────── */}
        <section style={sectionStyle}>
          <div style={sectionHeadStyle}>
            <h4 style={sectionTitleStyle}>Current balances</h4>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              style={refreshBtnStyle}
              title="Re-read live balances from Horizon"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {err && <div style={errorBoxStyle}>{err}</div>}

          {!err && loading && !balances && (
            <div style={mutedStyle}>Loading balances…</div>
          )}

          {balances?.unfunded && (
            <div style={mutedStyle}>
              Agent account is unfunded — nothing to sweep. You can still revoke below.
            </div>
          )}

          {balances && !balances.unfunded && (
            <BalanceTable
              native={balances.native}
              credits={balances.credits}
              baseReserveXlm={balances.baseReserveXlm || '0'}
              subentryCount={balances.subentryCount || 0}
            />
          )}
        </section>

        {/* ── Target picker ──────────────────────────────────── */}
        <section style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Sweep target</h4>
          <div style={chipRowStyle}>
            <button
              type="button"
              onClick={() => setTarget(operatorWallet)}
              style={target === operatorWallet ? chipActiveStyle : chipStyle}
              title="Default — sweep into the operator wallet that's signing revoke."
            >
              Operator wallet
              <span style={chipMonoStyle}>{shortAddr(operatorWallet)}</span>
            </button>
            {passkeyTarget && (
              <button
                type="button"
                onClick={() => setTarget(passkeyTarget)}
                style={target === passkeyTarget ? chipActiveStyle : chipStyle}
                title="Send to your linked passkey smart account instead."
              >
                Send to passkey wallet instead
                <span style={chipMonoStyle}>{shortAddr(passkeyTarget)}</span>
              </button>
            )}
          </div>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Custom target (G… or C…)</span>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value.trim())}
              spellCheck={false}
              style={inputStyle}
              placeholder="G… (classic) or C… (smart-account)"
            />
          </label>
          {!targetValid && target.length > 0 && (
            <div style={inlineWarnStyle}>Not a valid Stellar address.</div>
          )}
          {target && !STELLAR_G.test(target) && (
            <div style={mutedStyle}>
              Target is a contract address — accountMerge is unavailable (requires G… destination).
            </div>
          )}
        </section>

        {/* ── Sweep options ──────────────────────────────────── */}
        <section style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Sweep options</h4>
          <label style={checkRow}>
            <input
              type="checkbox"
              checked={closeTL}
              onChange={(e) => setCloseTL(e.target.checked)}
              disabled={!balances || balances.credits.length === 0}
            />
            <div>
              <div style={checkTitleStyle}>Close trustlines after payment</div>
              <div style={checkHintStyle}>
                Frees the 0.5 XLM base reserve held by each token trustline. Recommended unless you
                want the trustlines to remain open.
              </div>
            </div>
          </label>
          <label style={checkRow}>
            <input
              type="checkbox"
              checked={merge}
              onChange={(e) => setMerge(e.target.checked)}
              disabled={!isClassicAgent || !STELLAR_G.test(target)}
            />
            <div>
              <div style={checkTitleStyle}>Merge agent account into target</div>
              <div style={checkHintStyle}>
                Closes the agent's classic Stellar account on-ledger and refunds the remaining base
                reserve. Requires the target to be a G… account. Cannot be undone.
              </div>
            </div>
          </label>
        </section>

        {/* ── Operations preview ─────────────────────────────── */}
        <section style={sectionStyle}>
          <h4 style={sectionTitleStyle}>What will happen</h4>
          <ol style={opsListStyle}>
            {balances && !balances.unfunded && balances.credits.length === 0 && !balances.native && (
              <li style={opItemStyle}>No assets to sweep.</li>
            )}
            {balances && balances.credits.map((c) => (
              <li key={`pay-${c.code}-${c.issuer}`} style={opItemStyle}>
                Send <strong>{c.balance}</strong> <code>{c.code}</code> → target
              </li>
            ))}
            {closeTL && balances && balances.credits.map((c) => (
              <li key={`ct-${c.code}-${c.issuer}`} style={opItemStyle}>
                Close trustline <code>{c.code}</code>:<code style={monoStyle}>{shortAddr(c.issuer)}</code>
              </li>
            ))}
            {balances?.native && (
              <li style={opItemStyle}>
                Send native XLM (minus reserves & fee buffer) → target
              </li>
            )}
            {merge && isClassicAgent && STELLAR_G.test(target) && (
              <li style={opItemStyle}>Merge agent account → target (refunds remaining XLM)</li>
            )}
            <li style={opStrongStyle}>
              Sign <code>revoke_agent</code> on the registry — agent is permanently retired.
            </li>
          </ol>
          <div style={mutedStyle}>{opsCount} sweep operation{opsCount === 1 ? '' : 's'} + 1 registry call</div>
        </section>

        {blockingReasons.length > 0 && (
          <div style={errorBoxStyle}>
            <strong>Cannot dispose yet:</strong>
            <ul style={{ margin: '6px 0 0 18px' }}>
              {blockingReasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        <label style={ackRowStyle}>
          <input
            type="checkbox"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
          />
          <span>
            I understand this is irreversible. The agent address cannot be re-registered, and the
            sweep will be executed by the agent's local key via the MCP <code>agent_dispose</code>
            tool.
          </span>
        </label>

        <div style={footerStyle}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
          <button
            onClick={() => onConfirmed({
              targetAccountId: target,
              closeTrustlines: closeTL,
              mergeAccount:    merge && isClassicAgent && STELLAR_G.test(target),
            })}
            disabled={!canConfirm}
            style={canConfirm ? confirmBtnStyle : confirmBtnDisabledStyle}
            title={
              !targetValid     ? 'Pick a valid sweep target.'
              : !acked          ? 'Confirm the irreversibility checkbox.'
              : blockingReasons.length > 0 ? 'Resolve blocking issues first.'
              : 'Run the dispose flow.'
            }
          >
            Sweep & revoke
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentDisposeModal;

// ── Sub-components ─────────────────────────────────────────────────────────

const BalanceTable: React.FC<{
  native:         { balance: string } | null;
  credits:        AgentBalanceCredit[];
  baseReserveXlm: string;
  subentryCount:  number;
}> = ({ native, credits, baseReserveXlm, subentryCount }) => (
  <div style={tableWrapStyle}>
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>Asset</th>
          <th style={thStyle}>Balance</th>
          <th style={thStyle}>Liabilities</th>
        </tr>
      </thead>
      <tbody>
        {native && (
          <tr>
            <td style={tdStyle}><code>XLM</code> (native)</td>
            <td style={tdStyle}>{native.balance}</td>
            <td style={tdStyle}>
              <span title="Reserved by Stellar protocol — cannot be swept.">
                reserve {baseReserveXlm} ({subentryCount} subentries)
              </span>
            </td>
          </tr>
        )}
        {credits.map((c) => (
          <tr key={`${c.code}-${c.issuer}`}>
            <td style={tdStyle}>
              <code>{c.code}</code>
              <span style={mutedSmallStyle}>{shortAddr(c.issuer)}</span>
            </td>
            <td style={tdStyle}>{c.balance}</td>
            <td style={tdStyle}>
              {parseFloat(c.sellingLiabilities) > 0 || parseFloat(c.buyingLiabilities) > 0
                ? `sell ${c.sellingLiabilities} / buy ${c.buyingLiabilities}`
                : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Helpers ────────────────────────────────────────────────────────────────

function shortAddr(a: string | undefined | null): string {
  if (!a) return '';
  if (a.length <= 12) return a;
  return `${a.slice(0, 5)}…${a.slice(-5)}`;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 70,
};

const panelStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 640,
  maxHeight: '92vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  padding: 20,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 8,
};

const kickerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#9a3412',
};

const titleStyle: React.CSSProperties = {
  margin: '4px 0 4px',
  fontSize: 20,
  fontWeight: 700,
  color: '#111827',
};

const agentAddrStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#374151',
  wordBreak: 'break-all',
};

const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  fontSize: 26,
  lineHeight: 1,
  cursor: 'pointer',
  color: '#6b7280',
};

const leadStyle: React.CSSProperties = {
  margin: '8px 0 4px',
  fontSize: 13,
  color: '#374151',
};

const sectionStyle: React.CSSProperties = { marginTop: 16 };

const sectionHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
};

const refreshBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  color: '#111827',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 8,
  fontSize: 13,
  marginBottom: 8,
};

const infoBoxStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#fffbeb',
  color: '#92400e',
  borderRadius: 8,
  fontSize: 13,
  marginTop: 8,
  border: '1px solid #fde68a',
};

const mutedStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 12,
};

const mutedSmallStyle: React.CSSProperties = {
  color: '#9ca3af',
  fontSize: 10,
  fontFamily: 'monospace',
  marginLeft: 6,
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#fff',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  fontSize: 12,
  fontWeight: 700,
  color: '#374151',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'top',
};

const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 8,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid #d1d5db',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  color: '#111827',
};

const chipActiveStyle: React.CSSProperties = {
  ...chipStyle,
  borderColor: '#4f46e5',
  background: '#eef2ff',
  color: '#3730a3',
};

const chipMonoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  fontWeight: 500,
  color: '#6b7280',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 8,
};

const labelTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontFamily: 'monospace',
  fontSize: 12,
};

const inlineWarnStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#b91c1c',
};

const checkRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 0',
  cursor: 'pointer',
};

const checkTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
};

const checkHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
};

const opsListStyle: React.CSSProperties = {
  margin: '4px 0 8px 18px',
  padding: 0,
  fontSize: 13,
  color: '#374151',
};

const opItemStyle: React.CSSProperties = {
  marginBottom: 4,
};

const opStrongStyle: React.CSSProperties = {
  ...opItemStyle,
  fontWeight: 600,
  color: '#9a3412',
  marginTop: 6,
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  color: '#6b7280',
  marginLeft: 6,
};

const ackRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 8,
  background: '#fffbeb',
  border: '1px solid #fde68a',
  fontSize: 12,
  color: '#78350f',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 16,
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#111827',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const confirmBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 999,
  border: '1px solid #9a3412',
  background: '#9a3412',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const confirmBtnDisabledStyle: React.CSSProperties = {
  ...confirmBtnStyle,
  background: '#fcd9c1',
  borderColor: '#fcd9c1',
  color: '#9a3412',
  cursor: 'not-allowed',
};
